import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, Lock, ArrowLeft, Clock, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DyvysSeriesDetailPage() {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [series, setSeries] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userAccess, setUserAccess] = useState({});

  useEffect(() => {
    fetchSeriesDetail();
  }, [seriesId]);

  useEffect(() => {
    if (user && episodes.length > 0) {
      checkUserAccess();
    }
  }, [user, episodes]);

  const fetchSeriesDetail = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dyvys/series/${seriesId}`);
      if (!response.ok) throw new Error("Série introuvable");
      
      const data = await response.json();
      setSeries(data.series);
      setEpisodes(data.episodes || []);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Impossible de charger la série");
      navigate("/dyvys");
    } finally {
      setLoading(false);
    }
  };

  const checkUserAccess = async () => {
    if (!user) return;

    // Vérifier d'abord l'accès à toute la série
    try {
      const seriesAccessResponse = await fetch(`${API_URL}/api/dyvys/check-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          item_type: "series",
          item_id: seriesId,
          user_id: user.user_id
        })
      });
      const seriesAccessData = await seriesAccessResponse.json();
      
      if (seriesAccessData.has_access) {
        // L'utilisateur a accès à toute la série
        const allAccess = {};
        episodes.forEach(ep => {
          allAccess[ep.episode_id] = true;
        });
        setUserAccess(allAccess);
        return;
      }

      // Sinon, vérifier l'accès individuel pour chaque épisode
      const accessChecks = {};
      for (const episode of episodes) {
        if (episode.is_free) {
          accessChecks[episode.episode_id] = true;
        } else {
          const response = await fetch(`${API_URL}/api/dyvys/check-access`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              item_type: "episode",
              item_id: episode.episode_id,
              user_id: user.user_id
            })
          });
          const data = await response.json();
          accessChecks[episode.episode_id] = data.has_access;
        }
      }
      setUserAccess(accessChecks);
    } catch (error) {
      console.error("Erreur vérification accès:", error);
    }
  };

  const handleEpisodeClick = (episode) => {
    if (!user) {
      toast.error("Veuillez vous connecter pour regarder");
      navigate("/login");
      return;
    }

    if (episode.is_free || userAccess[episode.episode_id]) {
      // L'utilisateur a accès, ouvrir le lecteur
      navigate(`/dyvys/watch/${episode.episode_id}`);
    } else {
      // L'utilisateur n'a pas accès, rediriger vers le paiement
      navigate(`/dyvys/checkout/episode/${episode.episode_id}`);
    }
  };

  const handleBuySeries = () => {
    if (!user) {
      toast.error("Veuillez vous connecter pour acheter");
      navigate("/login");
      return;
    }
    navigate(`/dyvys/checkout/series/${seriesId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-lg">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!series) {
    return null;
  }

  // Calculer le prix total de la série
  const totalSeriesPrice = episodes.reduce((sum, ep) => sum + (ep.price_cdf || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black text-white">
      {/* Header */}
      <header className="border-b border-purple-800/50 backdrop-blur-sm bg-black/30">
        <div className="container mx-auto px-4 py-6">
          <Button
            onClick={() => navigate("/dyvys")}
            variant="ghost"
            className="text-purple-300 hover:text-purple-100 hover:bg-purple-500/20"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au catalogue
          </Button>
        </div>
      </header>

      {/* Series Info */}
      <section className="py-12 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="md:col-span-1">
              {series.cover_image_url ? (
                <img
                  src={series.cover_image_url}
                  alt={series.title}
                  className="w-full rounded-lg shadow-2xl"
                />
              ) : (
                <div className="w-full aspect-[2/3] bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                  <Play className="w-24 h-24 text-white opacity-50" />
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{series.title}</h1>
              
              {series.category && (
                <Badge className="bg-purple-500/20 text-purple-300 mb-4">
                  {series.category}
                </Badge>
              )}

              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                {series.description || "Aucune description disponible."}
              </p>

              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-2 bg-gray-800/50 rounded-lg px-4 py-2">
                  <Play className="w-5 h-5 text-purple-400" />
                  <span className="text-sm">{episodes.length} épisodes</span>
                </div>
                <div className="flex items-center gap-2 bg-gray-800/50 rounded-lg px-4 py-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  <span className="text-sm">Série complète: {totalSeriesPrice} CDF</span>
                </div>
              </div>

              <Button
                onClick={handleBuySeries}
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-lg px-8"
              >
                <Play className="w-5 h-5 mr-2" />
                Acheter toute la série ({totalSeriesPrice} CDF)
              </Button>
            </div>
          </div>

          {/* Episodes List */}
          <div>
            <h2 className="text-3xl font-bold mb-6">Épisodes</h2>
            
            {episodes.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="w-16 h-16 text-purple-400 mx-auto mb-4 opacity-50" />
                <p className="text-xl text-gray-400">Aucun épisode disponible</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {episodes.map((episode) => {
                  const hasAccess = episode.is_free || userAccess[episode.episode_id];
                  
                  return (
                    <Card
                      key={episode.episode_id}
                      className="bg-gradient-to-r from-gray-800/50 to-purple-900/30 border-purple-500/30 hover:border-purple-500 transition-all duration-300 cursor-pointer"
                      onClick={() => handleEpisodeClick(episode)}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center gap-6">
                          {/* Episode Thumbnail */}
                          <div className="relative flex-shrink-0">
                            {episode.thumbnail_url ? (
                              <img
                                src={episode.thumbnail_url}
                                alt={episode.title}
                                className="w-32 h-20 object-cover rounded-lg"
                              />
                            ) : (
                              <div className="w-32 h-20 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                                <Play className="w-8 h-8 text-white opacity-50" />
                              </div>
                            )}
                            {!hasAccess && (
                              <div className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center">
                                <Lock className="w-6 h-6 text-white" />
                              </div>
                            )}
                          </div>

                          {/* Episode Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-purple-400 font-bold">Épisode {episode.episode_number}</span>
                              {episode.is_free && (
                                <Badge className="bg-green-500/20 text-green-300">Gratuit</Badge>
                              )}
                              {hasAccess && !episode.is_free && (
                                <Badge className="bg-blue-500/20 text-blue-300">Acheté</Badge>
                              )}
                            </div>
                            <h3 className="text-xl font-semibold text-white mb-1">{episode.title}</h3>
                            {episode.description && (
                              <p className="text-gray-400 text-sm line-clamp-2">{episode.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-3 text-sm text-gray-400">
                              {episode.duration_minutes && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {episode.duration_minutes} min
                                </span>
                              )}
                              {!episode.is_free && (
                                <span className="flex items-center gap-1 text-green-400">
                                  <DollarSign className="w-4 h-4" />
                                  {episode.price_cdf} CDF
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Action Button */}
                          <div className="flex-shrink-0">
                            {hasAccess ? (
                              <Button className="bg-green-600 hover:bg-green-700">
                                <Play className="w-4 h-4 mr-2" />
                                Regarder
                              </Button>
                            ) : (
                              <Button className="bg-purple-600 hover:bg-purple-700">
                                <Lock className="w-4 h-4 mr-2" />
                                Acheter
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
